import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import { resolveCellMergeCodes } from '../../../../lib/rteTableHelper';

const StyledCell = styled.div`
  padding: ${(props) => props.padding || 8}px;
  border-right: ${(props) => props.border};
  border-bottom: ${(props) => props.border};
  flex: 1 1 0;
  min-width: 80px;
  box-sizing: border-box;
  overflow: hidden;
  font-size: ${(props) => props.fontSize || 14}px;
  color: ${(props) => props.fontColor || 'inherit'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  word-break: break-word;
`;

const StyledPill = styled.span`
  display: inline-block;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: ${(props) => props.fontSize || 12}px;
  font-weight: 500;
  color: ${(props) => props.textColor || '#fff'};
  background-color: ${(props) => props.bgColor || '#e0e0e0'};
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
`;

const StyledText = styled.span``;

const StyledImage = styled.img`
  max-width: ${(props) => props.maxWidth ? `${props.maxWidth}px` : '100%'};
  max-height: ${(props) => props.maxHeight ? `${props.maxHeight}px` : '80px'};
  object-fit: contain;
  border-radius: 4px;
  vertical-align: middle;
  margin: 2px;
`;

const getTextColor = (bgColor) => {
  if (!bgColor || bgColor.length < 7) return '#fff';
  const r = parseInt(bgColor.slice(1, 3), 16);
  const g = parseInt(bgColor.slice(3, 5), 16);
  const b = parseInt(bgColor.slice(5, 7), 16);
  const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
  return luminance > 150 ? '#000' : '#fff';
};

const HEADING_FONT_SIZES = { 1: 32, 2: 24, 3: 18.72, 4: 16, 5: 13.28, 6: 10.72 };

const TableFieldRteCell = ({
  templateJson,
  row,
  tableColumns,
  fieldPositionTableColumns,
  mode,
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  padding,
  borderWidth,
  borderColor,
  backgroundColor,
}) => {

  const segments = useMemo(
    () => resolveCellMergeCodes(templateJson, row, tableColumns, mode, fieldPositionTableColumns),
    [templateJson, row, tableColumns, mode, fieldPositionTableColumns]
  );

  const border = useMemo(
    () => `${borderWidth || 1}px solid ${borderColor || '#e0e0e0'}`,
    [borderWidth, borderColor]
  );

  return (
    <StyledCell
      fontSize={fontSize}
      fontColor={fontColor}
      fontStyle={fontStyle}
      fontWeight={fontWeight}
      textAlign={textAlign}
      textTransform={textTransform}
      textDecoration={textDecoration}
      padding={padding}
      border={border}
      backgroundColor={backgroundColor}
    >
      {segments.map((segment, i) => {
        if (segment.isBreak) {
          return <br key={i} />;
        }
        if (segment.isImage && segment.urls) {
          return segment.urls.map((url, j) => (
            <StyledImage
              key={`${i}-${j}`}
              src={url}
              maxWidth={segment.maxWidth}
              maxHeight={segment.maxHeight}
            />
          ));
        }
        const markStyle = {};
        if (segment.bold || segment.headingLevel) markStyle.fontWeight = 'bold';
        if (segment.italic) markStyle.fontStyle = 'italic';
        if (segment.underline && segment.strike) markStyle.textDecoration = 'underline line-through';
        else if (segment.underline) markStyle.textDecoration = 'underline';
        else if (segment.strike) markStyle.textDecoration = 'line-through';
        if (segment.headingLevel) markStyle.fontSize = `${HEADING_FONT_SIZES[segment.headingLevel] || 16}px`;
        if (segment.isPill && segment.color) {
          return (
            <StyledPill
              key={i}
              bgColor={segment.color}
              textColor={getTextColor(segment.color)}
              fontSize={fontSize ? fontSize - 2 : 12}
              style={Object.keys(markStyle).length > 0 ? markStyle : undefined}
            >
              {segment.text}
            </StyledPill>
          );
        }
        const textStyle = { ...markStyle };
        if (segment.textStyleColor) textStyle.color = segment.textStyleColor;
        return (
          <StyledText
            key={i}
            style={Object.keys(textStyle).length > 0 ? textStyle : undefined}
          >
            {segment.text}
          </StyledText>
        );
      })}
    </StyledCell>
  );

};

TableFieldRteCell.propTypes = {
  templateJson: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  row: PropTypes.object,
  tableColumns: PropTypes.array,
  fieldPositionTableColumns: PropTypes.object,
  mode: PropTypes.string,
  fontSize: PropTypes.number,
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.string,
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  padding: PropTypes.number,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
};

const arePropsEqual = (prev, next) => {
  if (prev.mode !== next.mode) return false;
  if (prev.fontSize !== next.fontSize) return false;
  if (prev.fontColor !== next.fontColor) return false;
  if (prev.fontStyle !== next.fontStyle) return false;
  if (prev.fontWeight !== next.fontWeight) return false;
  if (prev.textAlign !== next.textAlign) return false;
  if (prev.textTransform !== next.textTransform) return false;
  if (prev.textDecoration !== next.textDecoration) return false;
  if (prev.padding !== next.padding) return false;
  if (prev.borderWidth !== next.borderWidth) return false;
  if (prev.borderColor !== next.borderColor) return false;
  if (prev.backgroundColor !== next.backgroundColor) return false;
  if (prev.templateJson !== next.templateJson && !isEqual(prev.templateJson, next.templateJson)) return false;
  if (prev.row !== next.row && !isEqual(prev.row, next.row)) return false;
  if (prev.tableColumns !== next.tableColumns && !isEqual(prev.tableColumns, next.tableColumns)) return false;
  if (prev.fieldPositionTableColumns !== next.fieldPositionTableColumns && !isEqual(prev.fieldPositionTableColumns, next.fieldPositionTableColumns)) return false;
  return true;
};

export default memo(TableFieldRteCell, arePropsEqual);
